import { useEffect, useMemo, useState } from 'react';
import type { Address, CurrentUser, Order, VendorPublic } from '@arbuz/shared';
import { api } from '../api';
import { makeT } from '../i18n';
import { haptic, showBackButton } from '../tg';
import { AddressScreen } from './AddressScreen';
import { Catalog } from './Catalog';
import { Checkout } from './Checkout';
import { MyOrders } from './MyOrders';
import { OrderSuccess } from './OrderSuccess';
import { ProductSelect } from './ProductSelect';
import { SharePhoneButton } from './SharePhoneButton';
import { VendorSelect } from './VendorSelect';
import { BottomAction, ClientTopBar, IconCircle } from './clientUi';

/**
 * Экран выбора исполнителя стоит между главной и товарами: кто повезёт —
 * зависит от адреса, а что и почём — от того, кого выбрали. Показать товар
 * раньше исполнителя значило бы показать цены, которые потом изменятся.
 */
type ClientScreen =
  | 'welcome'
  | 'setup'
  | 'address'
  | 'home'
  | 'vendors'
  | 'productSelect'
  | 'checkout'
  | 'success'
  | 'orders';

const BACK_TO: Record<ClientScreen, ClientScreen | null> = {
  welcome: null,
  setup: 'welcome',
  address: 'setup',
  home: null,
  vendors: 'home',
  productSelect: 'vendors',
  checkout: 'productSelect',
  success: null,
  orders: 'home',
};

type AddressReturn = 'setup' | 'vendors' | 'productSelect' | 'checkout';

type AddressLoad =
  | { status: 'loading' }
  | { status: 'ready'; addresses: Address[] }
  | { status: 'error' };

export function ClientApp({ user }: { user: CurrentUser }) {
  const t = makeT(user.locale);
  const [screen, setScreen] = useState<ClientScreen>('welcome');
  const [addressLoad, setAddressLoad] = useState<AddressLoad>({ status: 'loading' });
  const [lastOrder, setLastOrder] = useState<Order | null>(null);
  const [addressReturn, setAddressReturn] = useState<AddressReturn>('setup');
  /**
   * Выбранный исполнитель. Живёт в памяти сессии, а не в хранилище: его
   * доступность зависит от адреса и рабочего дня, и вчерашний выбор сегодня
   * может быть недействителен. Корзина при этом помнит id витрины — чтобы
   * поймать несоответствие и предложить выбрать заново.
   */
  const [vendor, setVendor] = useState<VendorPublic | null>(null);

  useEffect(() => {
    void loadAddresses();
  }, []);

  useEffect(() => {
    const back = BACK_TO[screen];
    if (!back) return;
    return showBackButton(() => setScreen(back));
  }, [screen]);

  const addresses = useMemo(
    () => (addressLoad.status === 'ready' ? addressLoad.addresses : []),
    [addressLoad],
  );
  const defaultAddress = useMemo(
    () => addresses.find((a) => a.is_default) ?? addresses[0] ?? null,
    [addresses],
  );
  const setupComplete = user.has_phone && Boolean(defaultAddress);

  useEffect(() => {
    if (setupComplete && (screen === 'welcome' || screen === 'setup')) {
      setScreen('home');
    }
  }, [screen, setupComplete]);

  async function loadAddresses(): Promise<void> {
    try {
      setAddressLoad({ status: 'loading' });
      setAddressLoad({ status: 'ready', addresses: await api.get<Address[]>('/api/addresses') });
    } catch {
      setAddressLoad({ status: 'error' });
    }
  }

  function openAddress(next: AddressReturn = 'setup'): void {
    setAddressReturn(next);
    setScreen('address');
  }

  function onAddressSaved(address: Address): void {
    setAddressLoad((prev) => {
      const current = prev.status === 'ready' ? prev.addresses : [];
      const rest = current.filter((a) => a.id !== address.id).map((a) => ({ ...a, is_default: false }));
      return { status: 'ready', addresses: [address, ...rest] };
    });
    setScreen(addressReturn === 'setup' ? 'home' : addressReturn);
    setAddressReturn('setup');
  }

  if (addressLoad.status === 'loading') {
    return (
      <main className="client-page">
        <p className="muted">…</p>
      </main>
    );
  }

  if (addressLoad.status === 'error') {
    return (
      <main className="client-page">
        <h1 className="h1">{t('loadError')}</h1>
        <button
          type="button"
          className="btn-primary"
          style={{ width: '100%', marginTop: 'var(--s-4)' }}
          onClick={() => void loadAddresses()}
        >
          {t('retry')}
        </button>
      </main>
    );
  }

  switch (screen) {
    case 'setup':
      return (
        <ClientSetupScreen
          user={user}
          address={defaultAddress}
          onBack={() => setScreen('welcome')}
          onAddAddress={() => openAddress('setup')}
          onContinue={() => {
            haptic('light');
            if (!defaultAddress) openAddress('setup');
            else setScreen('home');
          }}
        />
      );
    case 'address':
      return <AddressScreen user={user} onSaved={onAddressSaved} />;
    case 'home':
      return (
        <Catalog
          user={user}
          address={defaultAddress}
          onCheckout={() => setScreen('vendors')}
          onOrders={() => setScreen('orders')}
        />
      );
    case 'vendors':
      return (
        <VendorSelect
          user={user}
          address={defaultAddress}
          onBack={() => setScreen('home')}
          onPicked={(picked) => {
            setVendor(picked);
            setScreen('productSelect');
          }}
          onChangeAddress={() => openAddress('vendors')}
        />
      );
    case 'productSelect':
      return (
        <ProductSelect
          user={user}
          address={defaultAddress}
          vendor={vendor}
          onBack={() => setScreen('vendors')}
          onCheckout={() => setScreen('checkout')}
          onChangeAddress={() => openAddress('productSelect')}
          onChangeVendor={() => setScreen('vendors')}
        />
      );
    case 'checkout':
      return vendor ? (
        <Checkout
          user={user}
          vendor={vendor}
          onNeedAddress={() => openAddress('checkout')}
          onNeedVendor={() => setScreen('vendors')}
          onDone={(order) => {
            setLastOrder(order);
            setScreen('success');
          }}
        />
      ) : (
        // Исполнителя нет — оформлять нечего: возвращаем к выбору
        <VendorSelect
          user={user}
          address={defaultAddress}
          onBack={() => setScreen('home')}
          onPicked={(picked) => {
            setVendor(picked);
            setScreen('productSelect');
          }}
          onChangeAddress={() => openAddress('vendors')}
        />
      );
    case 'orders':
      return <MyOrders user={user} onBack={() => setScreen('home')} />;
    case 'success':
      return lastOrder ? (
        <OrderSuccess
          user={user}
          order={lastOrder}
          onOrders={() => setScreen('orders')}
          onHome={() => setScreen('home')}
        />
      ) : (
        <Catalog
          user={user}
          address={defaultAddress}
          onCheckout={() => setScreen('vendors')}
          onOrders={() => setScreen('orders')}
        />
      );
    case 'welcome':
    default:
      return (
        <WelcomeScreen
          user={user}
          onStart={() => {
            haptic('light');
            setScreen(setupComplete ? 'home' : 'setup');
          }}
        />
      );
  }
}

function WelcomeScreen({ user, onStart }: { user: CurrentUser; onStart: () => void }) {
  const t = makeT(user.locale);
  const [welcomeFirstLine, welcomeSecondLine] = t('welcomeTitle').split('\n');

  return (
    <main className="client-page client-landing">
      <div className="client-landing-copy">
        <h1 className="client-landing-title">
          {welcomeFirstLine}
          <br />
          {welcomeSecondLine}
        </h1>
        <p className="muted" style={{ maxWidth: 260, margin: 'var(--s-5) auto 0' }}>
          {t('welcomeText')}
        </p>
      </div>
      <BottomAction transparent>
        <button type="button" className="btn-primary" style={{ width: '100%' }} onClick={onStart}>
          {t('orderWatermelon')}
        </button>
        <button type="button" className="client-ghost-button">
          {t('howWorks')}
        </button>
      </BottomAction>
    </main>
  );
}

function ClientSetupScreen({
  user,
  address,
  onBack,
  onAddAddress,
  onContinue,
}: {
  user: CurrentUser;
  address: Address | null;
  onBack: () => void;
  onAddAddress: () => void;
  onContinue: () => void;
}) {
  const t = makeT(user.locale);
  const ready = user.has_phone && Boolean(address);

  return (
    <main className="client-page with-bottom-bar">
      <ClientTopBar title={t('setupTitle')} onBack={onBack} />
      <h1 className="h1">{t('setupHeading')}</h1>
      <p className="muted" style={{ margin: 'var(--s-2) 0 0' }}>
        {t('setupText')}
      </p>

      <div className="client-setup-stack">
        <section className="client-card client-setup-card">
          <div className="client-row">
            <IconCircle icon="phone" />
            <div>
              <h2 style={{ margin: 0, fontSize: 'var(--fs-h3)' }}>{t('phoneTitle')}</h2>
              <p className="muted" style={{ margin: 'var(--s-1) 0 0', fontSize: 'var(--fs-sm)' }}>
                {user.has_phone ? user.phone : t('phoneTelegramOnly')}
              </p>
            </div>
            {user.has_phone && <span style={{ color: 'var(--c-primary-text)' }}>✓</span>}
          </div>
          {!user.has_phone && <SharePhoneButton locale={user.locale} />}
        </section>

        <section className="client-card client-setup-card">
          <div className="client-row" style={{ gridTemplateColumns: '38px 1fr' }}>
            <IconCircle icon="location" />
            <div>
              <h2 style={{ margin: 0, fontSize: 'var(--fs-h3)' }}>{t('addressTitle')}</h2>
              <p className="muted" style={{ margin: 'var(--s-1) 0 0', fontSize: 'var(--fs-sm)' }}>
                {address ? address.address_text : t('addressSetupHint')}
              </p>
            </div>
          </div>
          <div className="client-map-preview" style={{ marginTop: 'var(--s-3)' }}>
            <span className="client-map-pin" />
          </div>
          <button
            type="button"
            className="btn-primary"
            style={{ width: '100%', marginTop: 'var(--s-3)' }}
            onClick={onAddAddress}
          >
            {address ? t('changeAddress') : t('addAddress')}
          </button>
        </section>
      </div>

      <BottomAction>
        <button
          type="button"
          className="btn-primary"
          style={{ width: '100%' }}
          disabled={!user.has_phone}
          onClick={onContinue}
        >
          {ready ? t('continueOrder') : address ? t('continueOrder') : t('addAddress')}
        </button>
      </BottomAction>
    </main>
  );
}
